import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, Observable, of, switchMap, tap } from 'rxjs';

import { HelperService } from '@app/shared/services/helper.service';
import { NotificationService } from '@app/shared/services/notification.service';
import { FormSubmittedService } from '@shared/services/form-submitted.service';
import { SasGazetteerService } from '@shared/services/sas-gazetter.service';

@Component({
    selector: 'sas-street-field-type',
    standalone: true,
    imports: [ReactiveFormsModule, NgbTypeaheadModule, TranslatePipe, NgClass, FormsModule],
    template: `
        <div class="mb-3 form-group">
            @if (field.props.label?.length) {
            <label class="form-label"
                    [for]="field.id"
                    [textContent]="field.props.label"
                    translate="{{field.props.label}}">
            </label>
            }
            @if(field.props.required){
            <span> *</span>
            }
            <div class="d-inline-block" [title]="field.props['helptext'] | translate">
                @if(field.props['helptext'] && !isReadonly){
                <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                }
            </div>
            <input autocomplete="off"
                   tabindex="0"
                   type="search"
                   [attr.aria-invalid]="false"
                   class="form-control mb-2"
                   [editable]="false"
                   [focusFirst]="false"
                   [(ngModel)]="streetInputSelected"
                   [id]="field.id"
                   [inputFormatter]="inputFormatter"
                   maxlength="{{field.props.maxLength}}"
                   [minlength]="2"
                   name="{{field.name}}"
                   [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || formSubmittedService.isSubmitted())}"
                   [ngbTypeahead]="search"
                   placeholder="{{field.props.placeholder! | translate}}"
                   required="{{field.props.required}}"
                   [resultFormatter]="formatter"
                   [showHint]="true"
                   [readonly]="isReadonly"
                   [title]="field.props.label! | translate"
                   (selectItem)="selectStreet($event.item)"/>
            @if (isLoading) {
            <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            }
            @if (noResults && !isLoading) {
            <i class="bi bi-x-lg"></i> {{ 'STREET.NO_RESULTS' | translate }}
            }
        </div>
    `,
    styleUrl: './sas-street-field-type.component.scss'
})
export class SasStreetFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    private gazetteerService = inject(SasGazetteerService);
    private helperService = inject(HelperService);
    private notificationService = inject(NotificationService);
    private cdr = inject(ChangeDetectorRef);

    street: any = null;
    streetInputSelected: string = '';
    isLoading: boolean = false;
    noResults: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    constructor() {
        super();
    }

    ngOnInit() {
        if (
            typeof this.formControl.value === 'string' &&
            this.formControl.value.length &&
            this.containsOnlyNumbers(this.formControl.value)
        ) {
            this.getStreetFromUSRN(this.formControl.value);
        }
    }

    private containsOnlyNumbers(str: string) {
        return /^[0-9]+$/.test(str);
    }

    private getStreetFromUSRN(usrn: string) {
        this.gazetteerService.retrieveStreet(parseInt(usrn)).subscribe({
            next: (res) => {
                if (this.helperService.isObject(res) && res.usrn !== undefined) {
                    this.street = structuredClone(res);
                    this.streetInputSelected = res.name;
                    this.formControl.setValue(res.name);
                }
            },
            error: (error) => this.notificationService.error(error, 'UNHANDLED_ERROR')
        });
    }

    search = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => {
                this.isLoading = true;
                this.cdr.detectChanges();
            }),
            switchMap(term => {
                if (term.length < 2) {
                    this.noResults = false;

                    return of([]);
                }

                return this.getStreets(term);
            }),
            tap(() => {
                this.isLoading = false;
                this.cdr.detectChanges();
            })
        );

    private getStreets(street: string): Observable<any> {
        return this.gazetteerService.getStreets(street, this.field.props['apiUrl']).pipe(
            tap(res => {
                this.noResults = res.length === 0;
                this.cdr.detectChanges();
            }),
            catchError(() => {
                this.noResults = true;
                this.cdr.detectChanges();

                return of([]);
            })
        );
    }

    inputFormatter = (street: any) => typeof street === 'string' ? street : street.name;

    formatter = (street: any) => {
        let res = '';

        if (street) {
            res = street.name;

            if (typeof street.locality === 'string' && street.locality.length && typeof street.town === 'string' && street.town.length) {
                return `${res} (${street.locality}, ${street.town})`;
            }
            else if (typeof street.locality === 'string' && street.locality.length) {
                return `${res} (${street.locality})`;
            }
            else if (typeof street.town === 'string' && street.town.length) {
                return `${res} (${street.town})`;
            }
        }

        return res;
    }

    selectStreet(street: any) {
        const isOnlyName = this.field.props['sasOnlyName'];
        let formControlValue = null;

        if (isOnlyName) {
            this.streetInputSelected = street.name;
            formControlValue = street.name;
        }
        else {
            this.streetInputSelected = structuredClone(street);
            formControlValue = structuredClone(street);
        }

        this.formControl.setValue(formControlValue);
    }
}
